import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { Pie, Bar } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faUsers, faDollarSign, faChartPie, faChartBar, faClipboardList, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import 'chart.js/auto';
import './Overview.css';

const Overview = () => {
  const [data, setData] = useState({
    stockCard: null,
    customerCard: null,
    samplesPieChart: null,
    topCustomers: null,
    samplesPickup: null,
    salesCard: null,
    upcomingSaleReturnReco: null,
    top5YearsWithSales: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const formatDate = (dateStr) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateStr).toLocaleDateString(undefined, options);
};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const stockCard = await axios.get(`${baseUrl}api/AdminDashboard/AdminDashStockCard`);
        const customerCard = await axios.get(`${baseUrl}api/AdminDashboard/AdminDashCustomerCard`);
        const samplesPieChart = await axios.get(`${baseUrl}api/AdminDashboard/StockGivenAsSamplesForPieChart`);
        const topCustomers = await axios.get(`${baseUrl}api/AdminDashboard/Top5CustomersWithSamples`);
        const samplesPickup = await axios.get(`${baseUrl}api/AdminDashboard/SamplesDueForPickup`);
        const salesCard = await axios.get(`${baseUrl}api/AdminDashboard/AdminDashSalesCard`);
        const upcomingSaleReturnReco = await axios.get(`${baseUrl}api/AdminDashboard/UpcomingSaleReturnReco`);
        const top5YearsWithSales = await axios.get(`${baseUrl}api/AdminDashboard/Top5YearsWithTheirSales`);

        setData({
          stockCard: stockCard.data,
          customerCard: customerCard.data,
          samplesPieChart: samplesPieChart.data,
          topCustomers: topCustomers.data,
          samplesPickup: samplesPickup.data,
          salesCard: salesCard.data,
          upcomingSaleReturnReco: upcomingSaleReturnReco.data,
          top5YearsWithSales: top5YearsWithSales.data,
        });
      } catch (error) {
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [baseUrl]);

  const processPieChartData = (apiData) => {
    const groupedData = apiData.reduce((acc, item) => {
      acc[item.stockGroup] = (acc[item.stockGroup] || 0) + item.quantity;
      return acc;
    }, {});

    const labels = Object.keys(groupedData);
    const quantities = Object.values(groupedData);

    return {
      labels,
      datasets: [
        {
          data: quantities,
          backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
          hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
        },
      ],
    };
  };

  const processBarChartData = (apiData) => {
    const labels = apiData.map((customer) => customer.customerName);
    const dataValues = apiData.map((customer) => customer.total);

    return {
      labels,
      datasets: [
        {
          label: 'Samples',
          data: dataValues,
          backgroundColor: '#36A2EB',
          hoverBackgroundColor: '#4BC0C0',
        },
      ],
    };
  };

  const processYearsBarChart = (apiData) => {
    const labels = apiData.map((year) => year.year);
    const sales = apiData.map((year) => year.sales);

    return {
      labels,
      datasets: [
        {
          label: 'Sales Over Years',
          data: sales,
          backgroundColor: '#36A2EB',
        },
      ],
    };
  };

  const pieChartData = data.samplesPieChart ? processPieChartData(data.samplesPieChart) : null;
  const barChartData = data.topCustomers ? processBarChartData(data.topCustomers) : null;
  const topYearsChartData = data.top5YearsWithSales ? processYearsBarChart(data.top5YearsWithSales) : null;

  return (
    <div className="overview">
      <div className="main-content">
        <div className="content-container">
          {loading ? (
            <div className="loading-container">
              <Spinner animation="border" />
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <>
              {/* First Row with StockCard, CustomerCard, SalesCard */}
              <Row className="first-row">
                <Col md={4}>
                  <Card className="stock-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faBox} /> Stock Card
                      </Card.Title>
                      <Card.Text>
                        <strong>Total Stock Categories:</strong> {data.stockCard?.totalStockCategories || 'N/A'}<br />
                        <strong>Total Stock Items:</strong> {data.stockCard?.totalStockItems || 'N/A'}<br />
                        <strong>Total Stock Quantity:</strong> {data.stockCard?.totalStockQuantity || 'N/A'}<br />
                        <strong>Total Stock Value:</strong> {data.stockCard?.totalStockValue || 'N/A'}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="customer-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faUsers} /> Customer Card
                      </Card.Title>
                      <Card.Text>
                        <strong>Total Customers:</strong> {data.customerCard?.totalCustomers || 'N/A'}<br />
                        <strong>Customers with Stock:</strong> {data.customerCard?.totalCustomersWithStock || 'N/A'}<br />
                        <strong>Customers Who Generated Sales:</strong> {data.customerCard?.totalCustomersWhoGeneratedSales || 'N/A'}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="sales-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faDollarSign} /> Sales Card
                      </Card.Title>
                      <Card.Text>
                        <strong>Sales To Date:</strong> {data.salesCard?.salesToDate || 'N/A'}<br />
                        <strong>Sales This Year:</strong> {data.salesCard?.salesThisYear || 'N/A'}<br />
                        <strong>Sales This Month:</strong> {data.salesCard?.salesThisMonth || 'N/A'}<br />
                        <strong>Sales This Week:</strong> {data.salesCard?.salesThisWeek || 'N/A'}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Second Row with BarChart (Top Customers), PieChart (Samples), BarChart (Top Years) */}
              <Row className="second-row">
                <Col md={4}>
                  <Card className="bar-chart-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faChartBar} /> Top 5 Customers with Samples
                      </Card.Title>
                      {barChartData ? (
                        <Bar data={barChartData} options={{ plugins: { legend: { display: false }}}} />
                      ) : (
                        'No data available'
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="pie-chart-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faChartPie} /> Samples Pie Chart
                      </Card.Title>
                      {pieChartData ? (
                        <Pie data={pieChartData} options={{ plugins: { legend: { display: false }}}} />
                      ) : (
                        'No data available'
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="top-years-sales-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faCalendarAlt} /> Top 5 Years with Sales
                      </Card.Title>
                      {topYearsChartData ? (
                        <Bar data={topYearsChartData} options={{ plugins: { legend: { display: false }}}} />
                      ) : (
                        'No data available'
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Third Row with Samples Due for Pickup, Sales Return Reco */}
              <Row className="third-row">
                {/* Samples Due for Pickup */}
                <Col md={6}>
                  <Card className="samples-pickup-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faClipboardList} /> Samples Due for Pickup
                      </Card.Title>
                      <div className="table-container">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Customer</th>
                              <th>Tel</th>
                              <th>Stock</th>
                              <th>Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.samplesPickup ? (
                              data.samplesPickup.slice(0, 100).map((item, index) => (
                                <tr key={index}>
                                  <td>{item.customerName}</td>
                                  <td><a href={`tel:${item.telephone}`}>{item.telephone}</a></td>
                                  <td>{item.stockDescription}</td>
                                  <td>{item.quantity}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="4">No data available</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Upcoming Sale Return Reconciliation */}
                <Col md={6}>
                  <Card className="sales-return-reco-card-container">
                    <Card.Body>
                      <Card.Title>
                        <FontAwesomeIcon icon={faCalendarAlt} /> Upcoming Sale or Return Reconciliation
                      </Card.Title>
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th>Telephone</th>
                            <th>Date</th>
                            <th>Total Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.upcomingSaleReturnReco ? (
                            data.upcomingSaleReturnReco.map((item, index) => (
                              <tr key={index}>
                                <td>{item.customer}</td>
                                <td><a href={`tel:${item.telNo}`}>{item.telNo}</a></td>
                                <td>{formatDate(item.recoDate)}</td>
                                <td>GHC{item.value}</td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="4">No data available</td></tr>
                          )}
                        </tbody>
                      </table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Overview;
